import React, { useState, useEffect, useMemo } from 'react'
import { useNotificationActions } from '@/contexts/NotificationContext'
import { usePOSStore } from '@/store/posStore'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { CHEQUE_STATUS_CONFIG, CHEQUE_STATUS_TRANSITIONS, SRI_LANKAN_BANKS } from '@/types/pos'
import type { Cheque, ChequeStatus, ChequeType } from '@/types/pos'

interface ChequesPageProps {
  embedded?: boolean
}

export const ChequesPage: React.FC<ChequesPageProps> = ({ embedded = false }) => {
  const {
    cheques,
    isLoading,
    fetchCheques,
    addCheque,
    updateCheque,
    deleteCheque,
    updateChequeStatus,
  } = usePOSStore()

  // Main tab: issued vs received
  const [activeTab, setActiveTab] = useState<ChequeType>('issued')

  const [showAddForm, setShowAddForm] = useState(false)
  const [editingCheque, setEditingCheque] = useState<Cheque | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<ChequeStatus | 'all'>('all')
  const [filterBank, setFilterBank] = useState('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sortBy, setSortBy] = useState<'issueDate' | 'amount'>('issueDate')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const { showSuccess, showError } = useNotificationActions()

  useEffect(() => {
    fetchCheques()
  }, [fetchCheques])

  const setQuickDate = (preset: 'today' | 'week' | 'month' | 'all') => {
    const now = new Date()
    if (preset === 'all') {
      setDateFrom('')
      setDateTo('')
    } else if (preset === 'today') {
      const today = now.toISOString().slice(0, 10)
      setDateFrom(today)
      setDateTo(today)
    } else if (preset === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      setDateFrom(weekAgo.toISOString().slice(0, 10))
      setDateTo(now.toISOString().slice(0, 10))
    } else if (preset === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      setDateFrom(monthAgo.toISOString().slice(0, 10))
      setDateTo(now.toISOString().slice(0, 10))
    }
  }

  const toggleSort = (field: 'issueDate' | 'amount') => {
    setUserSortActive(true)
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
  }

  // Smart default sort per status group:
  // - Pending/Bounced: oldest issue date first (needs action soonest)
  // - Deposited: oldest deposit date first (should clear first)
  // - Cleared: newest clear date first (recent history)
  // - Cancelled/Expired: newest issue date first (archive)
  const [userSortActive, setUserSortActive] = useState(false)

  const sortChequesForStatus = (list: Cheque[], status: ChequeStatus): Cheque[] => {
    // If user explicitly clicked a sort column, use that everywhere
    if (userSortActive) {
      return [...list].sort((a, b) => {
        let cmp = 0
        if (sortBy === 'issueDate') {
          cmp = new Date(a.issueDate).getTime() - new Date(b.issueDate).getTime()
        } else {
          cmp = (a.amount || 0) - (b.amount || 0)
        }
        return sortOrder === 'asc' ? cmp : -cmp
      })
    }

    return [...list].sort((a, b) => {
      switch (status) {
        case 'pending':
        case 'bounced':
          // Closest deposit date first — that's the deadline to act on
          // Cheques without a deposit date go to the bottom
          {
            const aDate = a.depositDate ? new Date(a.depositDate).getTime() : Infinity
            const bDate = b.depositDate ? new Date(b.depositDate).getTime() : Infinity
            return aDate - bDate
          }
        case 'deposited':
          // Newest deposit date first — most recently deposited on top
          return new Date(b.depositDate || b.issueDate).getTime() - new Date(a.depositDate || a.issueDate).getTime()
        case 'cleared':
          // Newest clear date first — recent history on top
          return new Date(b.clearDate || b.issueDate).getTime() - new Date(a.clearDate || a.issueDate).getTime()
        case 'cancelled':
        case 'expired':
        default:
          // Newest issue date first — archive
          return new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime()
      }
    })
  }

  // Filter cheques for the active tab (no global sort — sorting is per-status-group)
  const filteredCheques = useMemo(() => {
    return cheques.filter(c => {
      // Must match active tab
      if (c.type !== activeTab) return false

      const search = searchTerm.toLowerCase()
      const matchesSearch = !search ||
        (c.chequeNumber || '').toLowerCase().includes(search) ||
        (c.payee || '').toLowerCase().includes(search) ||
        (c.bankName || '').toLowerCase().includes(search) ||
        (c.notes || '').toLowerCase().includes(search)

      const matchesStatus = filterStatus === 'all' || c.status === filterStatus
      const matchesBank = filterBank === 'all' || c.bankName === filterBank

      let matchesDate = true
      if (dateFrom || dateTo) {
        const cDate = new Date(c.issueDate || c.createdAt as string).toISOString().slice(0, 10)
        if (dateFrom && cDate < dateFrom) matchesDate = false
        if (dateTo && cDate > dateTo) matchesDate = false
      }

      return matchesSearch && matchesStatus && matchesBank && matchesDate
    })
  }, [cheques, activeTab, searchTerm, filterStatus, filterBank, dateFrom, dateTo, sortBy, sortOrder])

  // Summary cards computed from filtered cheques (respects all filters)
  const summaryCards = useMemo(() => {
    const stats = { pending: { total: 0, count: 0 }, deposited: { total: 0, count: 0 }, cleared: { total: 0, count: 0 }, problem: { total: 0, count: 0 } }

    filteredCheques.forEach(c => {
      const amt = c.amount || 0
      if (c.status === 'pending') { stats.pending.total += amt; stats.pending.count += 1 }
      else if (c.status === 'deposited') { stats.deposited.total += amt; stats.deposited.count += 1 }
      else if (c.status === 'cleared') { stats.cleared.total += amt; stats.cleared.count += 1 }
      else { stats.problem.total += amt; stats.problem.count += 1 } // bounced, cancelled, expired
    })

    return stats
  }, [filteredCheques])

  // Tab-level totals (unfiltered, just by type) for the tab headers
  const tabTotals = useMemo(() => {
    const issued = { total: 0, count: 0 }
    const received = { total: 0, count: 0 }
    cheques.forEach(c => {
      const amt = c.amount || 0
      if (c.type === 'issued') { issued.total += amt; issued.count += 1 }
      else { received.total += amt; received.count += 1 }
    })
    return { issued, received }
  }, [cheques])

  // Unique banks from current tab's data
  const banksInData = useMemo(() => {
    const set = new Set(cheques.filter(c => c.type === activeTab).map(c => c.bankName).filter(Boolean))
    return Array.from(set).sort()
  }, [cheques, activeTab])

  // Check for duplicate cheque numbers
  const isDuplicateChequeNumber = (num: string, excludeId?: string) => {
    return cheques.some(c => c.chequeNumber === num && (c.id || c._id) !== excludeId)
  }

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    const form = new FormData(e.target as HTMLFormElement)
    const chequeNumber = (form.get('chequeNumber') as string).trim()

    if (isDuplicateChequeNumber(chequeNumber)) {
      if (!confirm(`Cheque #${chequeNumber} already exists. Add anyway?`)) {
        setIsSubmitting(false)
        return
      }
    }

    const data = {
      chequeNumber,
      type: form.get('type') as ChequeType,
      bankName: form.get('bankName') === 'Other'
        ? (form.get('customBank') as string || '').trim()
        : form.get('bankName') as string,
      payee: (form.get('payee') as string).trim(),
      amount: parseFloat(form.get('amount') as string),
      issueDate: form.get('issueDate') as string,
      depositDate: (form.get('depositDate') as string) || null,
      notes: (form.get('notes') as string) || null,
    }

    if (!data.bankName) {
      showError('Validation', 'Please enter a bank name.')
      setIsSubmitting(false)
      return
    }

    try {
      await addCheque(data)
      showSuccess('Cheque Added', `Cheque #${data.chequeNumber} for Rs.${data.amount.toLocaleString()} recorded.`)
      setShowAddForm(false)
      // Switch to the tab matching the new cheque's type
      setActiveTab(data.type)
    } catch (error: any) {
      showError('Failed', error?.response?.data?.message || 'Could not save cheque.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCheque) return
    setIsSubmitting(true)
    const form = new FormData(e.target as HTMLFormElement)
    const chequeNumber = (form.get('chequeNumber') as string).trim()
    const editId = editingCheque.id || editingCheque._id!

    if (isDuplicateChequeNumber(chequeNumber, editId)) {
      if (!confirm(`Cheque #${chequeNumber} already exists. Save anyway?`)) {
        setIsSubmitting(false)
        return
      }
    }

    const updates: Partial<Cheque> = {
      chequeNumber,
      type: form.get('type') as ChequeType,
      bankName: form.get('bankName') === 'Other'
        ? (form.get('customBank') as string || '').trim()
        : form.get('bankName') as string,
      payee: (form.get('payee') as string).trim(),
      amount: parseFloat(form.get('amount') as string),
      issueDate: form.get('issueDate') as string,
      depositDate: (form.get('depositDate') as string) || null,
      notes: (form.get('notes') as string) || null,
    }

    const newStatus = form.get('status') as ChequeStatus
    if (newStatus && newStatus !== editingCheque.status) {
      updates.status = newStatus
    }

    try {
      await updateCheque(editId, updates)
      showSuccess('Updated', 'Cheque details saved.')
      setEditingCheque(null)
    } catch (error: any) {
      showError('Update Failed', error?.response?.data?.message || 'Could not update cheque.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (cheque: Cheque) => {
    if (!confirm(`Delete cheque #${cheque.chequeNumber}?`)) return
    try {
      await deleteCheque(cheque.id || cheque._id!)
      showSuccess('Deleted', `Cheque #${cheque.chequeNumber} removed.`)
    } catch {
      showError('Delete Failed', 'Could not delete cheque.')
    }
  }

  const handleStatusChange = async (cheque: Cheque, newStatus: ChequeStatus) => {
    try {
      await updateChequeStatus(cheque.id || cheque._id!, newStatus)
      showSuccess('Status Updated', `Cheque #${cheque.chequeNumber} → ${CHEQUE_STATUS_CONFIG[newStatus].label}`)
    } catch (error: any) {
      showError('Failed', error?.response?.data?.message || 'Could not update status.')
    }
  }

  const handleExportCSV = () => {
    if (filteredCheques.length === 0) {
      showError('No data', 'No cheques to export.')
      return
    }
    const headers = ['Cheque No', 'Type', 'Bank', 'Payee', 'Amount', 'Issue Date', 'Deposit Date', 'Clear Date', 'Status', 'Notes']
    const rows = filteredCheques.map(c => [
      `"${c.chequeNumber}"`,
      c.type,
      `"${c.bankName}"`,
      `"${(c.payee || '').replace(/"/g, '""')}"`,
      (c.amount || 0).toFixed(2),
      c.issueDate ? new Date(c.issueDate).toLocaleDateString() : '',
      c.depositDate ? new Date(c.depositDate).toLocaleDateString() : '',
      c.clearDate ? new Date(c.clearDate).toLocaleDateString() : '',
      c.status,
      `"${(c.notes || '').replace(/"/g, '""')}"`,
    ])
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const today = new Date().toISOString().slice(0, 10)
    a.download = `cheques-${activeTab}${dateFrom ? `-from-${dateFrom}` : ''}${dateTo ? `-to-${dateTo}` : ''}-${today}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // Check if a pending cheque is overdue (issued > 90 days ago and still pending)
  const isOverdue = (cheque: Cheque) => {
    if (cheque.status !== 'pending') return false
    const issued = new Date(cheque.issueDate).getTime()
    const now = Date.now()
    return (now - issued) > 90 * 24 * 60 * 60 * 1000
  }

  // ── Form Modal ──────────────────────────────────────────────────────────
  const ChequeFormModal: React.FC<{ cheque: Cheque | null; defaultType?: ChequeType; onSubmit: (e: React.FormEvent) => void; onCancel: () => void }> = ({ cheque, defaultType, onSubmit, onCancel }) => {
    const isEdit = !!cheque
    const allowedStatuses = isEdit ? CHEQUE_STATUS_TRANSITIONS[cheque!.status] : []
    const [selectedBank, setSelectedBank] = useState(
      cheque?.bankName && !SRI_LANKAN_BANKS.includes(cheque.bankName) ? 'Other' : (cheque?.bankName || 'Bank of Ceylon')
    )
    const [customBank, setCustomBank] = useState(
      cheque?.bankName && !SRI_LANKAN_BANKS.includes(cheque.bankName) ? cheque.bankName : ''
    )

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
        <div className="glass-card w-full max-w-lg border-primary/30 max-h-[90vh] overflow-y-auto">
          <h2 className="text-2xl font-bold esales-text mb-6">
            {isEdit ? 'Edit Cheque' : 'Add New Cheque'}
          </h2>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Type */}
              <div className="md:col-span-2">
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Type *</label>
                <div className="flex gap-2 mt-1">
                  {(['issued', 'received'] as ChequeType[]).map(t => (
                    <label key={t} className="flex-1">
                      <input type="radio" name="type" value={t} defaultChecked={cheque?.type === t || (!cheque && t === (defaultType || 'issued'))} className="peer hidden" />
                      <div className={`text-center py-3 rounded-xl border cursor-pointer font-bold text-sm uppercase tracking-wider transition-all
                        ${t === 'issued'
                          ? 'border-purple-500/30 peer-checked:border-purple-400 peer-checked:bg-purple-500/10 peer-checked:text-purple-400'
                          : 'border-cyan-500/30 peer-checked:border-cyan-400 peer-checked:bg-cyan-500/10 peer-checked:text-cyan-400'
                        }
                        hover:bg-muted/30`}>
                        {t === 'issued' ? 'Issued (We Gave)' : 'Received (We Got)'}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Cheque Number */}
              <div>
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Cheque Number *</label>
                <input name="chequeNumber" required defaultValue={cheque?.chequeNumber || ''} placeholder="e.g. 001234" className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
              </div>

              {/* Bank */}
              <div>
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Bank *</label>
                <select
                  name="bankName"
                  value={selectedBank}
                  onChange={(e) => { setSelectedBank(e.target.value); if (e.target.value !== 'Other') setCustomBank(''); }}
                  className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none"
                >
                  {SRI_LANKAN_BANKS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                {selectedBank === 'Other' && (
                  <input
                    name="customBank"
                    value={customBank}
                    onChange={(e) => setCustomBank(e.target.value)}
                    placeholder="Enter bank name"
                    required
                    className="w-full mt-2 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                )}
              </div>

              {/* Payee / From */}
              <div className="md:col-span-2">
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">
                  {(cheque?.type || defaultType || 'issued') === 'received' ? 'Received From *' : 'Paid To *'}
                </label>
                <input name="payee" required defaultValue={cheque?.payee || ''} placeholder="Company or person name" className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
              </div>

              {/* Amount */}
              <div>
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Amount (LKR) *</label>
                <input name="amount" type="number" step="0.01" min="0.01" required defaultValue={cheque?.amount || ''} placeholder="0.00" className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none text-lg font-bold" />
              </div>

              {/* Issue Date */}
              <div>
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Issue Date *</label>
                <input name="issueDate" type="date" required defaultValue={cheque?.issueDate?.split('T')[0] || new Date().toISOString().split('T')[0]} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
              </div>

              {/* Deposit Date */}
              <div>
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Deposit Date</label>
                <input name="depositDate" type="date" defaultValue={cheque?.depositDate?.split('T')[0] || ''} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none" />
              </div>

              {/* Status (edit only) */}
              {isEdit && (
                <div>
                  <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Status</label>
                  <select name="status" defaultValue={cheque!.status} className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none">
                    <option value={cheque!.status}>{CHEQUE_STATUS_CONFIG[cheque!.status].label} (current)</option>
                    {allowedStatuses.map(s => (
                      <option key={s} value={s}>{CHEQUE_STATUS_CONFIG[s].label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Notes */}
              <div className="md:col-span-2">
                <label className="text-[10px] font-black uppercase text-muted-foreground tracking-widest pl-1">Notes</label>
                <textarea name="notes" rows={2} defaultValue={cheque?.notes || ''} placeholder="Additional details..." className="w-full mt-1 px-4 py-3 bg-background/50 border border-border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none resize-none" />
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <button type="button" onClick={onCancel} className="flex-1 py-4 bg-muted hover:bg-muted/80 rounded-xl font-bold text-xs uppercase tracking-widest transition-colors">Cancel</button>
              <button type="submit" disabled={isSubmitting} className="flex-1 py-4 bg-primary text-white rounded-xl font-bold text-xs uppercase tracking-widest glow-effect transition-colors disabled:opacity-50">
                {isSubmitting ? 'SAVING...' : isEdit ? 'SAVE CHANGES' : 'ADD CHEQUE'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // ── Status Action Buttons ────────────────────────────────────────────────
  const StatusActions: React.FC<{ cheque: Cheque }> = ({ cheque }) => {
    const transitions = CHEQUE_STATUS_TRANSITIONS[cheque.status]
    if (transitions.length === 0) return null

    const buttonStyles: Record<string, string> = {
      deposited: 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30',
      cleared: 'bg-green-500/20 text-green-400 hover:bg-green-500/30',
      bounced: 'bg-red-500/20 text-red-400 hover:bg-red-500/30',
      cancelled: 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30',
      expired: 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30',
      pending: 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30',
    }

    return (
      <div className="flex items-center gap-1.5">
        {transitions.map(s => (
          <button
            key={s}
            onClick={() => handleStatusChange(cheque, s)}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors ${buttonStyles[s] || 'bg-muted text-muted-foreground'}`}
          >
            {CHEQUE_STATUS_CONFIG[s].label}
          </button>
        ))}
      </div>
    )
  }

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className={embedded ? '' : 'min-h-screen bg-gradient-space p-3 sm:p-4 lg:p-6'}>
      <div className="w-full">
        {/* Header (only if standalone) */}
        {!embedded && (
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 sm:mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold esales-text font-display">Cheque Tracker</h1>
              <p className="text-muted-foreground mt-2">Track issued and received cheques.</p>
            </div>
          </div>
        )}

        {/* ── Issued / Received Tabs ────────────────────────────────────────── */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => { setActiveTab('issued'); setFilterStatus('all'); setFilterBank('all'); setSearchTerm(''); setUserSortActive(false) }}
            className={`flex-1 py-4 rounded-xl font-bold text-sm uppercase tracking-wider transition-all border-2 ${
              activeTab === 'issued'
                ? 'border-purple-400 bg-purple-500/10 text-purple-400 shadow-lg shadow-purple-500/10'
                : 'border-border bg-muted/20 text-muted-foreground hover:bg-muted/40'
            }`}
          >
            <div className="flex flex-col items-center gap-1">
              <span className="text-lg">Issued</span>
              <span className="text-xs opacity-70">{tabTotals.issued.count} cheques &middot; Rs.{tabTotals.issued.total.toLocaleString()}</span>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('received'); setFilterStatus('all'); setFilterBank('all'); setSearchTerm(''); setUserSortActive(false) }}
            className={`flex-1 py-4 rounded-xl font-bold text-sm uppercase tracking-wider transition-all border-2 ${
              activeTab === 'received'
                ? 'border-cyan-400 bg-cyan-500/10 text-cyan-400 shadow-lg shadow-cyan-500/10'
                : 'border-border bg-muted/20 text-muted-foreground hover:bg-muted/40'
            }`}
          >
            <div className="flex flex-col items-center gap-1">
              <span className="text-lg">Received</span>
              <span className="text-xs opacity-70">{tabTotals.received.count} cheques &middot; Rs.{tabTotals.received.total.toLocaleString()}</span>
            </div>
          </button>
        </div>

        {/* ── Summary Cards (filtered data) ─────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {([
            { label: 'Pending', data: summaryCards.pending, color: 'text-yellow-400', border: 'border-yellow-500/20', bg: 'bg-yellow-500/5' },
            { label: 'Deposited', data: summaryCards.deposited, color: 'text-blue-400', border: 'border-blue-500/20', bg: 'bg-blue-500/5' },
            { label: 'Cleared', data: summaryCards.cleared, color: 'text-green-400', border: 'border-green-500/20', bg: 'bg-green-500/5' },
            { label: 'Bounced / Cancelled', data: summaryCards.problem, color: 'text-red-400', border: 'border-red-500/20', bg: 'bg-red-500/5' },
          ]).map(({ label, data, color, border, bg }) => (
            <div key={label} className={`rounded-xl p-4 border ${border} ${bg}`}>
              <p className={`text-xl sm:text-2xl font-bold ${color}`}>Rs.{data.total.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold mt-1">
                {label} ({data.count})
              </p>
            </div>
          ))}
        </div>

        {/* ── Filters ───────────────────────────────────────────────────────── */}
        <div className="glass-card mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            <div className="col-span-2 sm:col-span-3 md:col-span-2">
              <input
                type="text"
                placeholder={`Search ${activeTab} cheques...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2.5 bg-background/50 border border-border rounded-lg placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:outline-none text-sm"
              />
            </div>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)} className="px-3 py-2 bg-background/50 border border-border rounded-lg text-sm">
              <option value="all">All Statuses</option>
              {Object.entries(CHEQUE_STATUS_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <select value={filterBank} onChange={(e) => setFilterBank(e.target.value)} className="px-3 py-2 bg-background/50 border border-border rounded-lg text-sm">
              <option value="all">All Banks</option>
              {banksInData.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <button onClick={() => setShowAddForm(true)} className="glow-button text-sm py-2.5">
              + Add Cheque
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
            <div className="flex items-center gap-2">
              <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-1.5 bg-background/50 border border-border rounded-lg text-sm" />
              <span className="text-muted-foreground text-xs">to</span>
              <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-1.5 bg-background/50 border border-border rounded-lg text-sm" />
              <div className="flex gap-1 ml-2">
                {(['all', 'today', 'week', 'month'] as const).map(preset => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setQuickDate(preset)}
                    className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-muted/50 hover:bg-muted/80 text-muted-foreground transition-colors uppercase tracking-wider"
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground">{filteredCheques.length} records</span>
              <button
                type="button"
                onClick={handleExportCSV}
                className="px-4 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700 transition-colors"
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* ── Cheque Table (grouped by status) ──────────────────────────────── */}
        {isLoading && cheques.length === 0 ? (
          <div className="glass-card">
            <LoadingSpinner size="md" message="Loading Cheques" submessage="Syncing records..." />
          </div>
        ) : filteredCheques.length === 0 ? (
          <div className="glass-card">
            <p className="py-12 text-center text-muted-foreground italic">
              {searchTerm || filterStatus !== 'all' || filterBank !== 'all' || dateFrom || dateTo
                ? `No ${activeTab} cheques match your filters.`
                : `No ${activeTab} cheques recorded yet. Click "+ Add Cheque" to get started.`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {(['pending', 'deposited', 'cleared', 'bounced', 'cancelled', 'expired'] as ChequeStatus[]).map(status => {
              const rawSection = filteredCheques.filter(c => c.status === status)
              if (rawSection.length === 0) return null
              const sectionCheques = sortChequesForStatus(rawSection, status)
              const cfg = CHEQUE_STATUS_CONFIG[status]
              const sectionTotal = sectionCheques.reduce((sum, c) => sum + (c.amount || 0), 0)

              return (
                <div key={status} className="glass-card overflow-hidden p-0">
                  {/* Status group header */}
                  <div className={`px-4 py-3 flex items-center justify-between ${cfg.bgColor}`}>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${cfg.bgColor} ${cfg.color}`}>
                        {cfg.label}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {sectionCheques.length} cheque{sectionCheques.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <span className={`text-lg font-bold ${cfg.color}`}>Rs.{sectionTotal.toLocaleString()}</span>
                  </div>

                  {/* Mobile card view */}
                  <div className="md:hidden divide-y divide-border/30">
                    {sectionCheques.map(cheque => {
                      const overdue = isOverdue(cheque)
                      return (
                        <div key={cheque.id || cheque._id} className={`p-4 ${overdue ? 'bg-red-500/5' : ''}`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="font-bold text-foreground">#{cheque.chequeNumber}</span>
                                {overdue && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500/20 text-red-400">OVERDUE</span>}
                              </div>
                              <div className="text-sm text-foreground font-medium">{cheque.payee}</div>
                              <div className="text-xs text-muted-foreground">{cheque.bankName}</div>
                            </div>
                            <span className={`font-bold whitespace-nowrap ${activeTab === 'issued' ? 'text-purple-400' : 'text-cyan-400'}`}>
                              Rs.{(cheque.amount || 0).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground mb-2">
                            <span>Issued: {new Date(cheque.issueDate).toLocaleDateString()}</span>
                            {cheque.depositDate && <span>Deposited: {new Date(cheque.depositDate).toLocaleDateString()}</span>}
                            {cheque.clearDate && <span>Cleared: {new Date(cheque.clearDate).toLocaleDateString()}</span>}
                            {cheque.notes && <span className="italic truncate max-w-[200px]">"{cheque.notes}"</span>}
                          </div>
                          <div className="flex items-center justify-between">
                            <StatusActions cheque={cheque} />
                            <div className="flex gap-3">
                              <button onClick={() => setEditingCheque(cheque)} className="text-[10px] font-bold text-primary hover:underline uppercase">Edit</button>
                              <button onClick={() => handleDelete(cheque)} className="text-[10px] font-bold text-red-400 hover:underline uppercase">Delete</button>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {/* Desktop table view */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-border/50 text-muted-foreground text-[10px] uppercase tracking-widest">
                          <th className="px-4 py-2.5 font-bold">Cheque No</th>
                          <th className="px-4 py-2.5 font-bold">Bank</th>
                          <th className="px-4 py-2.5 font-bold">{activeTab === 'issued' ? 'Paid To' : 'From'}</th>
                          <th className="px-4 py-2.5 font-bold cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => toggleSort('amount')}>
                            Amount {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </th>
                          <th className="px-4 py-2.5 font-bold cursor-pointer select-none hover:text-foreground transition-colors" onClick={() => toggleSort('issueDate')}>
                            Issue Date {sortBy === 'issueDate' && (sortOrder === 'asc' ? '↑' : '↓')}
                          </th>
                          <th className="px-4 py-2.5 font-bold">Deposit / Clear</th>
                          <th className="px-4 py-2.5 font-bold">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/30">
                        {sectionCheques.map(cheque => {
                          const overdue = isOverdue(cheque)
                          return (
                            <tr key={cheque.id || cheque._id} className={`hover:bg-muted/10 transition-colors ${overdue ? 'bg-red-500/5' : ''}`}>
                              <td className="px-4 py-3">
                                <span className="font-bold text-foreground">#{cheque.chequeNumber}</span>
                                {cheque.notes && <div className="text-[10px] text-muted-foreground truncate max-w-[150px] italic">"{cheque.notes}"</div>}
                              </td>
                              <td className="px-4 py-3 text-sm">{cheque.bankName}</td>
                              <td className="px-4 py-3 text-sm font-medium">{cheque.payee}</td>
                              <td className={`px-4 py-3 font-bold ${activeTab === 'issued' ? 'text-purple-400' : 'text-cyan-400'}`}>
                                Rs.{(cheque.amount || 0).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-sm">{new Date(cheque.issueDate).toLocaleDateString()}</td>
                              <td className="px-4 py-3 text-sm">
                                <div>
                                  {cheque.depositDate && <div className="text-blue-400 text-xs">Dep: {new Date(cheque.depositDate).toLocaleDateString()}</div>}
                                  {cheque.clearDate && <div className="text-green-400 text-xs">Clr: {new Date(cheque.clearDate).toLocaleDateString()}</div>}
                                  {!cheque.depositDate && !cheque.clearDate && <span className="text-muted-foreground">—</span>}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <StatusActions cheque={cheque} />
                                  <button onClick={() => setEditingCheque(cheque)} className="text-[10px] font-bold text-primary hover:underline uppercase">Edit</button>
                                  <button onClick={() => handleDelete(cheque)} className="text-[10px] font-bold text-red-400 hover:underline uppercase">Del</button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })}

            {/* Overall footer total */}
            <div className={`rounded-xl px-4 py-3 flex justify-between items-center ${activeTab === 'issued' ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-cyan-500/10 border border-cyan-500/20'}`}>
              <span className="text-xs text-muted-foreground font-bold uppercase tracking-wider">
                Total {activeTab} ({filteredCheques.length} cheque{filteredCheques.length !== 1 ? 's' : ''})
              </span>
              <span className={`text-lg font-bold ${activeTab === 'issued' ? 'text-purple-400' : 'text-cyan-400'}`}>
                Rs.{filteredCheques.reduce((sum, c) => sum + (c.amount || 0), 0).toLocaleString()}
              </span>
            </div>
          </div>
        )}

        {/* Add Modal */}
        {showAddForm && <ChequeFormModal cheque={null} defaultType={activeTab} onSubmit={handleAdd} onCancel={() => setShowAddForm(false)} />}

        {/* Edit Modal */}
        {editingCheque && <ChequeFormModal cheque={editingCheque} onSubmit={handleEdit} onCancel={() => setEditingCheque(null)} />}
      </div>
    </div>
  )
}
